import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { DismissWithReason } from "./dismiss-with-reason";

/**
 * `/agents/reviewer/learnings`: what the look back at Radim's meetings proposed.
 * Only an Active rule reaches Radim; a bug goes to Metoděj as a Hub ticket. A Word
 * (one his speech recognition got wrong) is active right away, Dismiss takes it out.
 */

type Status = "draft" | "active" | "dismissed";

type Learning = {
  id: string;
  kind: "rule" | "bug" | "asr";
  text: string;
  evidence: string;
  evidenceCount: number;
  status: Status;
  /** Why it was dismissed: Hub's reason, or the note of whoever dismissed it. */
  resolvedNote: string | null;
  createdAt: string;
  convertedGlobalId: number | null;
  project: { id: string; name: string } | null;
  session: {
    id: string;
    startedAt: string;
    mode: string;
    // Null for a planner conversation across all projects.
    project: { slug: string; name: string } | null;
  } | null;
  reviewedBy: { id: string; name: string | null } | null;
};

const TABS = [
  { key: "draft", label: "Draft" },
  { key: "active", label: "Active" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export function ReviewerLearningsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [mineMessage, setMineMessage] = useState<string | null>(null);

  const load = useCallback((t: Tab) => {
    setRows(null);
    return api
      .get<{ learnings: Learning[] }>(`/api/reviewer-learnings${t === "all" ? "" : `?status=${t}`}`)
      .then((res) => setRows(res.learnings))
      .catch(() => setError("Couldn't load learnings."));
  }, []);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function save(
    id: string,
    body: { status?: Status; text?: string; note?: string | undefined },
  ) {
    setBusyId(id);
    setError(null);
    try {
      await api.patch(`/api/reviewer-learnings/${id}`, body);
      setEditing(({ [id]: _, ...rest }) => rest);
      await load(tab);
    } catch {
      setError("Couldn't save that.");
    } finally {
      setBusyId(null);
    }
  }

  async function sendToMetodej(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.post(`/api/reviewer-learnings/${id}/send-to-metodej`, {});
      await load(tab);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send it.");
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMineMessage(null);
    try {
      await api.post("/api/reviewer-learnings/look-back", {});
      setMineMessage("Looking back at finished reviews, check back in a few minutes.");
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? "AI isn't configured."
          : "Couldn't start the look back.",
      );
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Radim's Learnings</h2>
          <p className="text-sm text-muted-foreground">
            After every finished review, Hubert reads the meeting back and proposes what Radim
            should do differently. A rule reaches Radim only once it's Active, from his next meeting
            on (spoken ones too), and taking it back removes it again. A bug is something in Hub's
            own tools: send it to Metoděj as a Hub ticket.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" variant="outline" size="sm" onClick={() => void mineNow()}>
            Look back now
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`reviewer-learnings-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="reviewer-learnings-empty">
          Nothing here.
        </p>
      )}

      {rows?.map((r) => {
        const draft = editing[r.id];
        return (
          <Card key={r.id} data-testid="reviewer-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant={r.kind === "bug" ? "destructive" : "default"}>
                  {r.kind === "bug" ? "Bug" : r.kind === "asr" ? "Word" : "Rule"}
                </Badge>
                <Badge variant="secondary">{r.project ? r.project.name : "Every project"}</Badge>
                <span>seen {r.evidenceCount}×</span>
                {r.session?.project && (
                  <Link
                    to="/projects/$slug/reviews"
                    params={{ slug: r.session.project.slug }}
                    search={{ review: r.session.id }}
                    className="hover:underline"
                  >
                    · from the {r.session.mode} review of {r.session.project.name},{" "}
                    {new Date(r.session.startedAt).toLocaleDateString("cs-CZ")}
                  </Link>
                )}
                {r.session && !r.session.project && (
                  <span>
                    · from a {r.session.mode} review across all projects,{" "}
                    {new Date(r.session.startedAt).toLocaleDateString("cs-CZ")}
                  </span>
                )}
                {r.convertedGlobalId && <span>· sent as #{r.convertedGlobalId}</span>}
                {r.reviewedBy && r.status !== "draft" && (
                  <span>
                    · {r.status === "active" ? "activated" : "dismissed"} by {r.reviewedBy.name}
                  </span>
                )}
              </div>
              {r.status === "dismissed" && r.resolvedNote && (
                <p className="text-xs text-muted-foreground" data-testid="learning-resolved-note">
                  {r.reviewedBy ? "Why: " : "Dismissed by Hub: "}
                  {r.resolvedNote}
                </p>
              )}
              {draft === undefined ? (
                <p className="text-sm">{r.text}</p>
              ) : (
                <textarea
                  className="w-full rounded-md border bg-background p-2 text-sm"
                  rows={3}
                  value={draft}
                  aria-label="Learning text"
                  onChange={(e) => setEditing((prev) => ({ ...prev, [r.id]: e.target.value }))}
                />
              )}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Why</summary>
                <p className="mt-1 whitespace-pre-wrap">{r.evidence}</p>
              </details>
              <div className="flex flex-wrap gap-2">
                {draft !== undefined ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !draft.trim()}
                      onClick={() => void save(r.id, { text: draft })}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(({ [r.id]: _, ...rest }) => rest)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing((prev) => ({ ...prev, [r.id]: r.text }))}
                  >
                    Edit
                  </Button>
                )}
                {r.kind === "bug" && !r.convertedGlobalId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void sendToMetodej(r.id)}
                  >
                    Send to Metoděj
                  </Button>
                )}
                {r.kind !== "bug" && r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "active" })}
                  >
                    Activate
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <DismissWithReason
                    busy={busyId === r.id}
                    onDismiss={(note) => void save(r.id, { status: "dismissed", note })}
                  />
                )}
                {r.status !== "draft" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "draft" })}
                  >
                    Back to draft
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
